import type { ReactNode } from 'react';

import { Badge, Box, Stack, Typography } from '@mui/material';

import { Icon } from '@karehero/llama/components/atoms/Icon/Icon';
import { radius } from '@karehero/llama/theme/radius';

import { CHROME_ZOOM, PILL_H } from '@/designs-dev/inspect/chrome';

// Loft's floating bars: the pills under a prototype (DrillModeToggle,
// StateSelector) and the bar on a flows page's canvas. One look, so they read
// as the same chrome wherever they float.
export const Pill = ({
  children,
  spacing = 0.5,
}: {
  children: ReactNode;
  spacing?: number;
}) => (
  <Stack
    sx={{
      zoom: CHROME_ZOOM,
      height: PILL_H,
      p: '4px',
      alignItems: 'center',
      bgcolor: 'background.paper',
      border: 1,
      borderColor: 'custom.neutral.200',
      borderRadius: `${radius.md}px`,
    }}
    direction='row'
    spacing={spacing}
  >
    {children}
  </Stack>
);

// One segment of a pill: an icon, or a few words. `tone='error'` is for a
// state that must not be mistaken for the current one (an old version).
// `count` badges the icon, for something waiting to be looked at (comments);
// `alert` turns the badge coral when some of it is new.
export const Segment = ({
  icon,
  children,
  selected = false,
  tone,
  count = 0,
  alert = false,
  onClick,
  label,
}: {
  icon?: string;
  children?: ReactNode;
  selected?: boolean;
  tone?: 'error';
  count?: number;
  alert?: boolean;
  // Without one, the segment is a label: it says something, it does nothing.
  onClick?: () => void;
  label: string;
}) => (
  <Box
    onClick={onClick}
    aria-label={label}
    sx={{
      px: 2,
      py: 1,
      gap: 0.5,
      borderRadius: '8px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      whiteSpace: 'nowrap',
      bgcolor: selected ? 'custom.neutral.100' : 'transparent',
      color:
        tone === 'error'
          ? 'error.main'
          : selected
            ? 'primary.main'
            : 'text.secondary',
      cursor: onClick ? 'pointer' : 'default',
      transition: 'background-color 150ms ease',
    }}
  >
    {icon && (
      <Badge
        badgeContent={count}
        max={99}
        sx={{
          '& .MuiBadge-badge': {
            bgcolor: alert ? 'custom.coral.600' : 'custom.charcoal',
            color: 'common.white',
            height: 16,
            minWidth: 16,
            px: 0.5,
            fontSize: 10,
            // Out of the icon's way, toward the corner of the segment.
            top: -2,
            right: -4,
          },
        }}
      >
        <Icon icon={icon as never} fontSize={18} />
      </Badge>
    )}
    {children && (
      <Typography variant='body2' sx={{ color: 'inherit' }}>
        {children}
      </Typography>
    )}
  </Box>
);
